import i18n from 'i18next';

/**
 * gLabel — localize a DB-driven gamification label by its SLUG.
 *
 * CON-1606. Every string on My Points comes from Neon:
 *
 *   gamification.point_action_definitions        → 'action'      (54 rows)
 *   gamification.level_definitions               → 'level'       (5)
 *   gamification.badge_definitions               → 'badge'       (9) + 'badgeDesc'
 *   gamification.venue_tier_definitions          → 'tier'        (5)
 *   gamification.venue_point_action_definitions  → 'venueAction' (21)
 *
 * The API returns them pre-rendered as `.label`, and every render site bound
 * that straight into JSX — so "Registered a payment", "Ran a WiFi speed test"
 * and "Scout / Explorer / Guide" stayed English in all 12 locales while the
 * chrome around them was translated. Patrick found it on the Vietnamese,
 * French and German My Points pages.
 *
 * `apps/backend/scripts/i18n-extract-gamification.mjs` registers each slug as
 * `gamification.<kind>.<slug>` in en.json, so they are now ordinary keys that
 * the normal translation pipeline (packets → validate → merge → gate) covers.
 * Re-run that script whenever a definition row is added; the new slug then
 * flows to every locale like any other string.
 *
 * Falls back to the English label the API already sent, so an un-synced slug
 * degrades to today's behaviour rather than rendering a raw `wifi_test`.
 *
 * Module-scope safe: uses the i18next singleton, not the hook, because several
 * call sites are formatters and config maps outside React.
 */
export function gLabel(kind, slug, fallback) {
  if (!slug) return fallback || '';
  return i18n.t(`gamification.${kind}.${slug}`, fallback ?? slug);
}

/** A point-ledger event / earnable action — `ev.action`, `a.action`. */
export const gAction = (slug, fallback) => gLabel('action', slug, fallback);
/** A user level — Scout, Explorer, Guide, Insider, Legend. */
export const gLevel = (slug, fallback) => gLabel('level', slug, fallback);
/** A badge name, and its one-line "how you earned it" description. */
export const gBadge = (slug, fallback) => gLabel('badge', slug, fallback);
export const gBadgeDesc = (slug, fallback) => gLabel('badgeDesc', slug, fallback);
/** Venue-side: tier name (Listed…Diamond) and venue point actions. */
export const gTier = (slug, fallback) => gLabel('tier', slug, fallback);
export const gVenueAction = (slug, fallback) => gLabel('venueAction', slug, fallback);

export default gLabel;
